import moment from 'moment';

/**
 * 點下訂房增速的某根柱 → 該去查哪兩段區間。
 *
 * 兩張圖的差別只是「哪一段是桶、哪一段是窗口」：
 *  - 依訂房日 (book)：柱 = 一個訂房日桶，窗口 = 前瞻住用窗口
 *  - 依入住日 (stay)：柱 = 一個住用日桶，窗口 = 近 N 個訂房日
 * 端點的貢獻公式對兩者相同，所以只要把桶與窗口填對位置就會與柱高一致。
 *
 * **一定要夾在窗口內**：柱是由後端在窗口內的資料聚出來的，桶本身卻是日曆邊界。
 * 第一根與最後一根柱的桶通常會超出窗口 (例如以「月」聚合時，最舊那根柱只有月底幾天有資料)，
 * 不夾就會把柱從來沒算過的訂單也列出來 —— 而且不會有任何錯誤訊號，
 * 只會讓明細合計莫名其妙大於柱高。
 */

const ISO = 'YYYY/MM/DD';

const clampRange = (start, end, win) => {
  const from = moment.max(start, win.from.clone().startOf('day'));
  const to = moment.min(end, win.to.clone().startOf('day'));
  return to.isBefore(from, 'day') ? null : { from, to };
};

/**
 * @param {object} opts
 * @param {'stay'|'book'} opts.mode      點的是哪張圖
 * @param {moment} opts.bucketStart      該柱所屬桶的起始時刻
 * @param {'day'|'isoWeek'|'month'} opts.unit  桶單位 (隨檢視粒度而定)
 * @param {{from: moment, to: moment}} opts.stayWindow  前瞻住用窗口
 * @param {{from: moment, to: moment}} opts.bookWindow  訂房日窗口 (近 N 日)
 * @returns {{stayFrom: string, stayTo: string, bookFrom: string, bookTo: string}|null}
 *          桶與窗口完全不重疊時回 null (正常情況不會發生，但不該靜默查出一段錯的區間)
 */
export function pickupOrdersRange({ mode, bucketStart, unit, stayWindow, bookWindow }) {
  const start = bucketStart.clone().startOf(unit);
  const end = bucketStart.clone().endOf(unit).startOf('day');
  const win = mode === 'book' ? bookWindow : stayWindow;
  const bucket = clampRange(start, end, win);
  if (!bucket) return null;

  const stay = mode === 'book' ? { from: stayWindow.from, to: stayWindow.to } : bucket;
  const book = mode === 'book' ? bucket : { from: bookWindow.from, to: bookWindow.to };
  return {
    stayFrom: stay.from.format(ISO), stayTo: stay.to.format(ISO),
    bookFrom: book.from.format(ISO), bookTo: book.to.format(ISO),
  };
}

/**
 * 貢獻欄的單位。住用區間只有一天時，貢獻就等於間數 (重疊天數恆為 1)，
 * 標成「間夜」會讓人以為和間數欄是兩回事；跨多天時才是真的間夜。
 */
export const contributionUnit = (stayFrom, stayTo) => (stayFrom === stayTo ? '間' : '間夜');
